import type {
  AIAlert,
  AIRecommendation,
  QuoteAnalysisInput,
  QuoteAnalysisResult,
} from '../types'

/**
 * QuoteAnalyzer — detects margin, profit, stock and price variance issues.
 */
export class QuoteAnalyzer {
  analyze(input: QuoteAnalysisInput): QuoteAnalysisResult {
    const targetMargin = input.targetMargin ?? 20
    const lowMarginItems: string[] = []
    const negativeProfitItems: string[] = []
    const outOfStockItems: string[] = []
    const highVarianceItems: string[] = []
    const risks: AIAlert[] = []
    const recommendations: AIRecommendation[] = []

    let totalValue = 0
    let marginWeighted = 0
    const supplierHits = new Map<string, number>()

    for (const item of input.items) {
      const subtotal = item.salePrice * item.quantity
      totalValue += subtotal
      marginWeighted += item.margin * subtotal

      if (item.supplier) {
        supplierHits.set(item.supplier, (supplierHits.get(item.supplier) ?? 0) + 1)
      }

      if (item.margin < targetMargin) {
        lowMarginItems.push(item.description)
        recommendations.push(
          rec(
            'margin',
            `Revisar margem de ${item.description}`,
            `Elevar venda para atingir ~${targetMargin}%`,
            `Margem atual ${item.margin.toFixed(1)}% abaixo da meta ${targetMargin}%`,
          ),
        )
      }

      if (item.profit < 0) {
        negativeProfitItems.push(item.description)
        risks.push(
          alert(
            'NEGATIVE_PROFIT',
            'danger',
            'Lucro negativo',
            `${item.description} gera prejuízo`,
            `Lucro unitário ${item.profit.toFixed(2)} < 0`,
          ),
        )
      }

      if (item.stock != null && item.stock <= 0) {
        outOfStockItems.push(item.description)
        risks.push(
          alert(
            'OUT_OF_STOCK',
            'warning',
            'Item sem estoque',
            `${item.description} sem disponibilidade`,
            'Estoque informado ≤ 0 — risco de não cumprir prazo',
          ),
        )
      }

      if (
        item.marketMinPrice != null &&
        item.marketMaxPrice != null &&
        item.marketMaxPrice > 0 &&
        item.marketMaxPrice - item.marketMinPrice >= item.marketMinPrice * 0.25
      ) {
        highVarianceItems.push(item.description)
        recommendations.push(
          rec(
            'price',
            `Alta variação de mercado: ${item.description}`,
            'Comparar fornecedores antes de fechar',
            `Amplitude de preço ${(
              ((item.marketMaxPrice - item.marketMinPrice) / item.marketMinPrice) *
              100
            ).toFixed(0)}% entre ofertas`,
          ),
        )
      }
    }

    totalValue = round2(totalValue)
    const averageMargin = totalValue > 0 ? round2(marginWeighted / totalValue) : 0

    if (averageMargin < targetMargin) {
      risks.push(
        alert(
          'LOW_MARGIN',
          'warning',
          'Margem média baixa',
          `Margem média ${averageMargin}% abaixo da meta ${targetMargin}%`,
          'Itens com margem comprimida reduzem rentabilidade do pedido',
        ),
      )
    }

    const recommendedSupplier =
      [...supplierHits.entries()].sort((a, b) => b[1] - a[1])[0]?.[0] ?? null

    const summary = [
      `Orçamento com ${input.items.length} itens e total ${totalValue.toFixed(2)}.`,
      `Margem média ${averageMargin}%.`,
      lowMarginItems.length
        ? `${lowMarginItems.length} item(ns) com margem baixa.`
        : 'Margens individuais dentro da meta.',
      negativeProfitItems.length
        ? `${negativeProfitItems.length} item(ns) com lucro negativo.`
        : 'Sem lucro negativo detectado.',
    ].join(' ')

    return {
      totalValue,
      averageMargin,
      recommendedSupplier,
      risks,
      recommendations,
      lowMarginItems,
      negativeProfitItems,
      outOfStockItems,
      highVarianceItems,
      summary,
    }
  }
}

function rec(
  kind: AIRecommendation['kind'],
  title: string,
  suggestion: string,
  reason: string,
): AIRecommendation {
  return {
    id: crypto.randomUUID(),
    kind,
    title,
    suggestion,
    reason,
    confidence: 0.78,
    requiresConfirmation: true,
  }
}

function alert(
  code: string,
  severity: AIAlert['severity'],
  title: string,
  message: string,
  reason: string,
): AIAlert {
  return {
    id: crypto.randomUUID(),
    code,
    severity,
    title,
    message,
    reason,
  }
}

function round2(n: number): number {
  return Math.round((n + Number.EPSILON) * 100) / 100
}

export const quoteAnalyzer = new QuoteAnalyzer()
